import { ChevronDown, ChevronUp, CreditCard, Pencil } from "lucide-react";
import { useState } from "react";

import type { DashboardSnapshot } from "../../../../lib/dashboard";
import {
  useProfileEdit,
  type UseProfileEditOptions,
  type UseProfileEditResult,
} from "../../../../lib/profile/use-profile-edit";
import type { ReadinessResult } from "../../../../lib/readiness-types";
import { SharpenPill } from "../../../readiness/sharpen-pill";
import { Select } from "../../../ui/select";
import { TierBadge } from "../../../ui/tier-badge";
import { sharpenMetaForRow } from "./sharpen-meta";
import { cn } from "../../../../lib/utils";
import type { ProfileRowTier } from "./tier-dot";
import { TierLadder } from "./tier-ladder";
import { CREDIT_BAND_LABELS, CREDIT_BAND_ITEMS } from "./credit-row.helpers";

export function CreditRow({
  snapshot,
  readiness,
  opts,
  onEdit,
}: {
  opts?: UseProfileEditOptions;
  snapshot: DashboardSnapshot;
  readiness: ReadinessResult | null | undefined;
  /**
   * When provided, the row renders a pencil affordance and calls onEdit()
   * to open the Edit & Verify drawer — consistent with AggregateInputRow.
   * When absent, the existing expand-in-place dropdown is preserved.
   */
  onEdit?: () => void;
}) {
  if (opts) {
    return (
      <EditableCreditRow
        snapshot={snapshot}
        readiness={readiness}
        opts={opts}
        onEdit={onEdit}
      />
    );
  }

  return (
    <CreditRowContent
      snapshot={snapshot}
      readiness={readiness}
      editProfile={null}
      onEdit={onEdit}
    />
  );
}

function EditableCreditRow({
  snapshot,
  readiness,
  opts,
  onEdit,
}: {
  opts: UseProfileEditOptions;
  snapshot: DashboardSnapshot;
  readiness: ReadinessResult | null | undefined;
  onEdit?: () => void;
}) {
  const editProfile = useProfileEdit(opts);
  return (
    <CreditRowContent
      snapshot={snapshot}
      readiness={readiness}
      editProfile={editProfile}
      onEdit={onEdit}
    />
  );
}

function CreditRowContent({
  snapshot,
  readiness,
  editProfile,
  onEdit,
}: {
  editProfile: UseProfileEditResult | null;
  snapshot: DashboardSnapshot;
  readiness: ReadinessResult | null | undefined;
  onEdit?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const initialDraft = snapshot.creditBucket ?? "";
  const [draftState, setDraftState] = useState({
    base: initialDraft,
    value: initialDraft,
  });
  const draft =
    draftState.base === initialDraft ? draftState.value : initialDraft;
  const setDraft = (value: string) => {
    setDraftState({ base: initialDraft, value });
  };

  const value = draft
    ? (CREDIT_BAND_LABELS[draft] ?? draft)
    : null;

  // Phase 5: tier is always self-reported for credit (no financial input field
  // for credit score — only the bucket is in snapshot). Phase 6 can wire
  // verification tier from a future credit-pull field.
  const tier: ProfileRowTier = "self-reported";

  const sharpenMeta = sharpenMetaForRow("credit", readiness);
  const error = editProfile?.error
    ? "Couldn't save — please try again."
    : undefined;

  const handleCreditChange = (next: string) => {
    if (next === initialDraft) {
      setDraft(next);
      return;
    }
    setDraft(next);
    if (!editProfile) {
      return;
    }
    editProfile.mutate({ credit_score_range: next }).catch(() => {
      setDraft(initialDraft);
    });
  };

  return (
    <article
      // TODO(SILL-187 follow-up): accept idPrefix prop if ProfileView is ever embedded twice on a page.
      id="profile-row-credit"
      data-state={expanded ? "open" : "closed"}
      className="overflow-hidden p-0 transition-colors hover:bg-accent/35"
    >
      <button
        type="button"
        onClick={onEdit ? onEdit : () => setExpanded((v) => !v)}
        className="flex min-h-[4.5rem] w-full items-center justify-between gap-3 px-4 py-3.5 text-left sm:px-5"
        aria-label="Credit"
        aria-expanded={onEdit ? undefined : expanded}
        data-slot="interactive-control"
      >
        {/* LEFT: icon tile + title + muted detail (credit value) */}
        <span className="flex min-w-0 items-center gap-3">
          <span
            data-testid="aggregate-input-icon"
            data-tier={tier}
            className={cn(
              "grid size-9 shrink-0 place-items-center rounded-xl",
              tier === "self-reported"
                ? "bg-muted text-muted-foreground"
                : "bg-primary/10 text-primary",
            )}
          >
            <CreditCard aria-hidden="true" className="size-4" strokeWidth={2} />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-foreground">
              Credit
            </span>
            <span className="block truncate text-xs text-muted-foreground mt-0.5">
              {value ?? "Not set"}
            </span>
          </span>
        </span>
        {/* RIGHT: verification badge + edit affordance */}
        <span className="flex min-w-0 shrink-0 items-center justify-end gap-2">
          <TierBadge size="sm" tier={tier} />
          {onEdit ? (
            /* Drawer mode: pencil icon opens Edit & Verify drawer */
            <span
              role="button"
              tabIndex={0}
              aria-label="Edit Credit"
              data-slot="row-edit-trigger"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit();
                }
              }}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
            </span>
          ) : expanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </span>
      </button>

      {/* Expand-in-place mode only — suppressed when onEdit is wired */}
      {!onEdit && expanded && (
        <div className="border-t border-border-soft bg-background/60 px-4 pb-4 pt-4 sm:px-5">
          <div className="space-y-3" data-slot="value-region">
            <Select
              disabled={Boolean(editProfile?.isPending)}
              error={error}
              items={CREDIT_BAND_ITEMS}
              label="Credit band"
              onValueChange={handleCreditChange}
              placeholder="Select your band"
              value={draft}
            />
          </div>

          <div
            className="mt-4 space-y-3 border-t border-border-soft pt-4"
            data-slot="trust-region"
          >
            <div className="space-y-2">
              <p className="eyebrow-label text-[10px] tracking-[0.18em]">
                Verification
              </p>
              <TierLadder current={tier} />
            </div>
            {sharpenMeta ? (
              <SharpenPill
                delta={sharpenMeta.goal.projected_delta}
                action={sharpenMeta.goal.action_label}
                variant={sharpenMeta.variant}
              />
            ) : null}
          </div>
        </div>
      )}
    </article>
  );
}
